import { useDescope, useSession } from '@descope/react-sdk';
import { useNavigateToFirstWidget } from 'app/navigation';
import { ROUTES } from 'app/Views/routes';
import { QueryParam } from 'consts/keys';
import useEffectOnceWhen from 'hooks/useEffectOnceWhen';
import useQueryParams from 'hooks/useQueryParams';
import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { useAppInitialized } from './appInit';
import { useLoggedInUser, useTenant } from './user';
import { useWidgets } from './widget/widget';

// One-shot tenant init, run once after the app finishes loading. Consumes a
// ?tenantId= URL param: selects that tenant when it is a membership tenant not
// already active, then strips the param (PRESERVE_QUERY_PARAMS would otherwise
// keep it) so it is never read again this session. When it resolves on /select
// it moves into the app once widgets load. `bootstrapping` stays true while the
// param is being resolved, so the caller can hold the picker back.
export const useTenantBootstrap = () => {
	const appInitialized = useAppInitialized();
	const { user, isUserLoading } = useLoggedInUser();
	const { claims, isSessionLoading } = useSession();
	const { tenant } = useTenant();
	const { selectTenant } = useDescope();
	const isOnTenantSelectRoute = useMatch(ROUTES.tenantSelect);
	const [urlTenantId, setUrlTenantId] = useQueryParams(QueryParam.TenantId);
	const navigateToFirstWidget = useNavigateToFirstWidget();
	const widgets = useWidgets();

	const [bootstrapping, setBootstrapping] = useState(!!urlTenantId);
	const [leaveSelectRoute, setLeaveSelectRoute] = useState(false);

	const ready =
		appInitialized && !isUserLoading && !isSessionLoading && !!user && !!claims;

	useEffectOnceWhen(
		ready && !!urlTenantId,
		() => {
			(async () => {
				try {
					const member = user?.userTenants?.some(
						(t) => t.tenantId === urlTenantId
					);
					let selected = !!member && urlTenantId === tenant?.tenantId;
					if (member && urlTenantId !== tenant?.tenantId) {
						const res = await selectTenant(urlTenantId);
						selected = !!res?.ok;
					}
					if (selected && isOnTenantSelectRoute) {
						setLeaveSelectRoute(true);
					}
				} catch {
					// fall through to the picker
				} finally {
					setUrlTenantId(null);
					setBootstrapping(false);
				}
			})();
		},
		[urlTenantId, user, selectTenant]
	);

	// navigateToFirstWidget no-ops without widgets, which load after bootstrap;
	// wait for them and re-check the live /select match, then run once.
	useEffect(() => {
		if (leaveSelectRoute && widgets?.length) {
			setLeaveSelectRoute(false);
			if (isOnTenantSelectRoute) {
				navigateToFirstWidget();
			}
		}
	}, [leaveSelectRoute, widgets, isOnTenantSelectRoute, navigateToFirstWidget]);

	return { bootstrapping };
};
